import { Box, BoxProps, useColorModeValue } from "@chakra-ui/react";
import { makeContextMenu } from "components/context-menu";
import { UseContextMenuOptions } from "components/context-menu/types";
import React, { ReactNode } from "react";
import { Column, CustomTableProps, tableStyle } from "../types";

export const CustomRow = React.memo(
  function CustomRow({
    rowIndex,
    onClickRow,
    row,
    columns,
    rowStyle,
    isFooter,
    LineBellow,
    tableStyle = "custom",
    doubleClickInRow,
    numberOfRows,
    lineHoverColor: lineHoverColorCustom,
    constructContextMenu,
    openContextMenuOnMouseEnter,
    openContextMenuOnLeftClick,
    topRowStyle,
    constructRowId,
    animateRowsOnAppear,
    customRowPairColor,
  }: {
    rowIndex: number;
    onClickRow?: CustomTableProps["onClickRow"];
    row: any;
    columns: Column[];
    rowStyle?: CustomTableProps["rowStyle"];
    isFooter?: boolean;
    LineBellow?: (object: { row: any }) => ReactNode;
    tableStyle?: tableStyle;
    doubleClickInRow?: (row: any) => void;
    numberOfRows: number;
    lineHoverColor?: string;
    constructContextMenu?: (row: any) => Omit<UseContextMenuOptions, "e">;
    openContextMenuOnMouseEnter?: boolean;
    openContextMenuOnLeftClick?: boolean;
    topRowStyle?: BoxProps;
    constructRowId?: CustomTableProps["constructRowId"];
    animateRowsOnAppear?: boolean;
    customRowPairColor?: string;
  }) {
    const rowStyleReturn = rowStyle?.(row, rowIndex);
    const lineHoverColor =
      (lineHoverColorCustom ?? tableStyle === "classic")
        ? "var(--chakra-colors-hover-100)"
        : tableStyle === "custom"
          ? rowIndex % 2 === 0
            ? "rgba(0, 145, 197, 0.2)"
            : undefined
          : undefined;

    let bg =
      tableStyle === "custom"
        ? rowIndex % 2 === 0
          ? (customRowPairColor ?? "rgba(0, 143, 197, 0.1)")
          : ""
        : "";

    const cssTablePrefix =
      tableStyle === "custom"
        ? ""
        : tableStyle === "invisible"
          ? "-invisible"
          : "-classic";

    const classicBgColor = useColorModeValue(
      "white",
      "var(--chakra-colors-navy-700)",
    );

    if (tableStyle === "classic") {
      bg = classicBgColor;
    }

    const handleConstructContextMenu = (e: any) => {
      if (constructContextMenu !== undefined) {
        e.preventDefault();
        const contextMenuOptions = constructContextMenu(row);
        makeContextMenu({ ...contextMenuOptions, e: e });
      }
    };

    return (
      <>
        <Box
          as="tr"
          id={constructRowId?.(row)}
          _hover={
            lineHoverColor
              ? {
                  bg: `${lineHoverColor} !important`,
                }
              : {}
          }
          onMouseMove={(e: any) => {
            if (openContextMenuOnMouseEnter === true) {
              handleConstructContextMenu(e);
            }
          }}
          onClick={(e: any) => {
            if (openContextMenuOnLeftClick === true) {
              handleConstructContextMenu(e);
            }
            if (!isFooter && onClickRow) {
              onClickRow(row);
            }
            // isFooter ? undefined : onClickRow ? () => onClickRow(row) : undefined
          }}
          onContextMenu={(e: any) =>
            openContextMenuOnMouseEnter === false
              ? handleConstructContextMenu(e)
              : undefined
          }
          onDoubleClick={
            isFooter
              ? undefined
              : doubleClickInRow
                ? () => doubleClickInRow(row)
                : undefined
          }
          className={`table-row${onClickRow ? " cursor-pointer" : ""}${
            typeof rowStyleReturn === "string" ? " " + rowStyleReturn : ""
          }${cssTablePrefix}${
            animateRowsOnAppear ? " table-row-appear-animation" : ""
          }`}
          style={
            typeof rowStyleReturn === "string"
              ? undefined
              : isFooter
                ? { border: "none", background: "none", ...rowStyleReturn }
                : { background: bg, ...rowStyleReturn }
          }
          {...topRowStyle}
        >
          {columns.map(
            (
              {
                key,
                render,
                cellStyle,
                isVisible = true,
                rowTdProps,
                onClickInTd,
              },
              index,
            ) => {
              const headerClassName =
                tableStyle === "classic"
                  ? index === columns.length - 1
                    ? `table-data-cell-classic-last`
                    : "table-data-cell-classic"
                  : "table-data-cell";
              return isVisible ? (
                <td
                  key={`cell-${rowIndex}-${index}`}
                  className={headerClassName}
                  style={{ ...cellStyle?.(row, rowIndex) }}
                  onClick={(ev) => {
                    if (onClickInTd) {
                      ev.preventDefault();
                      ev.stopPropagation();
                      onClickInTd?.(row);
                    }
                  }}
                  {...rowTdProps}
                >
                  {render?.(row, rowIndex, rowIndex + 1 === numberOfRows) ??
                    row[key ?? ""]}
                </td>
              ) : null;
            },
          )}
        </Box>
        {LineBellow ? (
          <tr
            className={`table-row${cssTablePrefix}`}
            style={
              typeof rowStyleReturn === "string"
                ? undefined
                : isFooter
                  ? { border: "none", background: "none", ...rowStyleReturn }
                  : { background: bg, ...rowStyleReturn }
            }
          >
            <td colSpan={columns.length}>
              <LineBellow row={row} />
            </td>
          </tr>
        ) : null}
      </>
    );
  },
  (prev, next) => {
    if (!prev.constructRowId || !next.constructRowId) return false;
    return prev.constructRowId?.(prev.row) === next.constructRowId?.(next.row);
  },
);
